import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const StaffIntroduction = () => {
  const teamMembers = [
    {
      id: 1,
      name: "Chef Mehmet Özkan",
      role: "Executive Chef & Owner",
      image: "https://images.unsplash.com/photo-1583394293214-28ded15ee548?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
      bio: `Born in Istanbul and trained in traditional Turkish cuisine, Chef Mehmet brings over 25 years of culinary expertise to Kebab Palace. His passion for authentic Mediterranean flavors and modern presentation techniques has earned recognition from food critics and loyal customers alike.

His signature dishes blend time-honored family recipes with contemporary cooking methods, creating an unforgettable dining experience that honors tradition while embracing innovation.`,
      specialties: ["Traditional Turkish Kebabs", "Mediterranean Grilling", "Spice Blending"],
      languages: ["Turkish", "English", "Arabic"],
      experience: "25+ years",
      social: {
        instagram: "@chefmehmet_kebabpalace",
        linkedin: "mehmet-ozkan-chef"
      }
    },
    {
      id: 2,
      name: "Sofia Papadopoulos",
      role: "Sous Chef & Pastry Specialist",
      image: "https://images.unsplash.com/photo-1594736797933-d0401ba2fe65?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
      bio: `Sofia's Greek heritage and culinary school training in Athens create the perfect foundation for our Mediterranean desserts and appetizers. Her innovative approach to traditional sweets like baklava and galaktoboureko has become a customer favorite.

She believes that every meal should end with a sweet memory, and her creations consistently deliver that magical moment that keeps guests returning.`,
      specialties: ["Greek Pastries", "Mediterranean Desserts", "Appetizer Creation"],
      languages: ["Greek", "English", "Italian"],
      experience: "12+ years",
      social: {
        instagram: "@sofia_sweetmediterranean",
        linkedin: "sofia-papadopoulos-chef"
      }
    },
    {
      id: 3,
      name: "Marcus Rodriguez",
      role: "Restaurant Manager",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
      bio: `Marcus ensures every guest feels like family from the moment they walk through our doors. With extensive experience in hospitality management and a genuine passion for Mediterranean culture, he orchestrates seamless dining experiences.

His multilingual abilities and cultural sensitivity make every international guest feel welcomed and understood, creating the warm atmosphere that defines Kebab Palace.`,
      specialties: ["Guest Relations", "Event Coordination", "Staff Training"],
      languages: ["English", "Spanish", "Portuguese"],
      experience: "15+ years",
      social: {
        linkedin: "marcus-rodriguez-hospitality",
        twitter: "@marcus_kebabpalace"
      }
    },
    {
      id: 4,
      name: "Amira Hassan",
      role: "Head Server & Wine Specialist",
      image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
      bio: `Amira's expertise in Mediterranean wines and her intuitive understanding of flavor pairings elevate every dining experience. Her recommendations consistently surprise and delight guests, creating perfect harmony between food and wine.

Her warm personality and extensive knowledge of our menu make her the perfect guide for first-time visitors and a trusted friend to our regular customers.`,
      specialties: ["Wine Pairing", "Menu Knowledge", "Customer Service"],
      languages: ["Arabic", "English", "French"],
      experience: "8+ years",
      social: {
        instagram: "@amira_wineandkebab",
        linkedin: "amira-hassan-sommelier"
      }
    }
  ];

  const teamStats = [
    { label: "Combined Experience", value: "60+ Years", icon: "Award" },
    { label: "Languages Spoken", value: "8 Languages", icon: "Globe" },
    { label: "Culinary Awards", value: "12 Awards", icon: "Trophy" },
    { label: "Happy Customers", value: "50K+ Served", icon: "Heart" }
  ];

  return (
    <div className="space-y-12">
      {/* Header */}
      <div className="text-center">
        <h2 className="font-headline text-3xl font-bold text-text-primary mb-4">
          Meet Our Culinary Family
        </h2>
        <p className="font-body text-lg text-text-secondary max-w-3xl mx-auto">
          Behind every exceptional meal is a passionate team dedicated to sharing the authentic flavors 
          and warm hospitality of the Mediterranean. Get to know the people who make Kebab Palace special.
        </p>
      </div>

      {/* Team Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        {teamStats.map((stat, index) => (
          <div key={index} className="text-center p-6 bg-surface rounded-xl shadow-warm">
            <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name={stat.icon} size={28} className="text-primary" />
            </div>
            <div className="font-headline text-2xl font-bold text-text-primary mb-1">
              {stat.value}
            </div>
            <div className="text-sm text-text-secondary font-medium">
              {stat.label}
            </div>
          </div>
        ))}
      </div>

      {/* Team Members */}
      <div className="space-y-8">
        {teamMembers.map((member, index) => (
          <div key={member.id} className={`grid grid-cols-1 lg:grid-cols-3 gap-8 items-start p-8 bg-surface rounded-xl shadow-warm ${
            index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
          }`}>
            {/* Photo and Basic Info */}
            <div className={`space-y-4 ${index % 2 === 1 ? 'lg:col-start-3' : ''}`}>
              <div className="relative">
                <div className="aspect-w-3 aspect-h-4 rounded-xl overflow-hidden shadow-warm">
                  <Image
                    src={member.image}
                    alt={member.name}
                    className="w-full h-80 object-cover"
                  />
                </div>
                <div className="absolute -bottom-4 left-4 bg-white rounded-lg shadow-warm p-3">
                  <div className="flex items-center space-x-2">
                    <Icon name="Clock" size={16} className="text-accent-600" />
                    <span className="text-sm font-medium text-text-primary">{member.experience}</span>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="flex justify-center space-x-4">
                {member.social.instagram && (
                  <a
                    href={`https://instagram.com/${member.social.instagram.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gradient-to-r from-pink-500 to-purple-600 text-white p-2 rounded-lg hover:shadow-warm transition-organic"
                  >
                    <Icon name="Instagram" size={20} />
                  </a>
                )}
                {member.social.linkedin && (
                  <a
                    href={`https://linkedin.com/in/${member.social.linkedin}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-blue-600 text-white p-2 rounded-lg hover:shadow-warm transition-organic"
                  >
                    <Icon name="Linkedin" size={20} />
                  </a>
                )}
                {member.social.twitter && (
                  <a
                    href={`https://twitter.com/${member.social.twitter.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-blue-400 text-white p-2 rounded-lg hover:shadow-warm transition-organic"
                  >
                    <Icon name="Twitter" size={20} />
                  </a>
                )}
              </div>
            </div>

            {/* Bio and Details */}
            <div className={`lg:col-span-2 space-y-6 ${index % 2 === 1 ? 'lg:col-start-1' : ''}`}>
              <div>
                <h3 className="font-headline text-2xl font-bold text-text-primary mb-2">
                  {member.name}
                </h3>
                <p className="text-primary font-cta font-semibold text-lg mb-4">
                  {member.role}
                </p>
                <div className="prose prose-lg max-w-none text-text-secondary">
                  <p className="mb-4">{member.bio}</p>
                </div>
              </div>

              {/* Specialties */}
              <div>
                <h4 className="font-cta font-semibold text-lg text-text-primary mb-3 flex items-center">
                  <Icon name="ChefHat" size={20} className="text-accent-600 mr-2" />
                  Specialties
                </h4>
                <div className="flex flex-wrap gap-2">
                  {member.specialties.map((specialty, idx) => (
                    <span
                      key={idx}
                      className="bg-accent-100 text-accent-700 px-3 py-1 rounded-full text-sm font-medium"
                    >
                      {specialty}
                    </span>
                  ))}
                </div>
              </div>

              {/* Languages */}
              <div>
                <h4 className="font-cta font-semibold text-lg text-text-primary mb-3 flex items-center">
                  <Icon name="Globe" size={20} className="text-trust-600 mr-2" />
                  Languages
                </h4>
                <div className="flex flex-wrap gap-2">
                  {member.languages.map((language, idx) => (
                    <span
                      key={idx}
                      className="bg-trust-100 text-trust-700 px-3 py-1 rounded-full text-sm font-medium"
                    >
                      {language}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Join Our Team CTA */}
      <div className="bg-gradient-to-r from-primary-500 to-secondary-600 text-white rounded-xl p-8 text-center">
        <h3 className="font-headline text-2xl font-bold mb-4">
          Join Our Culinary Family
        </h3>
        <p className="text-white/90 mb-6 max-w-2xl mx-auto">
          We're always looking for passionate individuals who share our commitment to authentic Mediterranean 
          cuisine and exceptional hospitality. Discover career opportunities at Kebab Palace.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="mailto:careers@kebabpalace.com"
            className="bg-white text-primary hover:bg-primary-50 font-cta font-semibold px-8 py-3 rounded-lg shadow-warm transition-organic inline-flex items-center justify-center space-x-2"
          >
            <Icon name="Mail" size={20} />
            <span>Send Your Resume</span>
          </a>
          <a
            href="tel:+15551234567"
            className="bg-accent text-white hover:bg-accent-600 font-cta font-semibold px-8 py-3 rounded-lg shadow-warm transition-organic inline-flex items-center justify-center space-x-2"
          >
            <Icon name="Phone" size={20} />
            <span>Call About Opportunities</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default StaffIntroduction;